import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Bug,
  Clock,
  DollarSign,
  Rocket,
  ShieldCheck,
  Cpu,
  Workflow,
  Sparkles,
  Mail,
} from "lucide-react";
import MarqueeStrip from "../components/MarqueeStrip";

const EMAIL_HREF =
  "mailto:hello@pixelkare.com?subject=AI%20App%20Development%20Enquiry&body=Hi%20Pixelkare%2C%20I%27d%20like%20to%20discuss%20building%20an%20AI-powered%20app.";

const METRICS = [
  {
    icon: Clock,
    value: "1 Week",
    label: "Delivery Time",
    context: "vs 3–6 months at a traditional agency",
  },
  {
    icon: Bug,
    value: "~0%",
    label: "Bug Rate",
    context: "AI-verified, test-covered codebase",
  },
  {
    icon: DollarSign,
    value: "80%",
    label: "Cost Reduction",
    context: "A fraction of typical agency invoices",
  },
  {
    icon: ShieldCheck,
    value: "0",
    label: "Human-Error Risk",
    context: "No missed sprints. No vague handoffs.",
  },
];

const STEPS = [
  {
    n: "01",
    icon: Sparkles,
    title: "Idea → Spec",
    body: "We translate your idea into a precise product spec — flows, screens, data models, and integrations — in a single working session.",
  },
  {
    n: "02",
    icon: Cpu,
    title: "AI Architecture",
    body: "Our AI pipelines scaffold the app: backend APIs, frontend components, auth, payments, and the database schema — production-grade from day one.",
  },
  {
    n: "03",
    icon: Workflow,
    title: "Build & QA",
    body: "Automated code generation paired with continuous AI-driven test coverage. Every change is validated before it ships.",
  },
  {
    n: "04",
    icon: Rocket,
    title: "Go Live",
    body: "Deployed, monitored, market-ready. You walk away with a real product, not a prototype.",
  },
];

const COMPARE = [
  ["Timeline", "1 week", "3–6 months"],
  ["Cost", "Up to 80% lower", "Agency-tier invoices"],
  ["Bugs at launch", "Near zero", "Backlog from day one"],
  ["Scope creep", "Locked spec", "Endless revisions"],
  ["Risk of delay", "Zero", "High"],
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(31,135,188,0.10), transparent 50%), radial-gradient(circle at 80% 70%, rgba(31,135,188,0.06), transparent 55%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-32">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-10 lg:col-span-9">
              <p
                className="overline reveal"
                style={{ animationDelay: "0ms" }}
                data-testid="hero-eyebrow"
              >
                <span className="inline-block h-1.5 w-1.5 bg-[#1f87bc] mr-3 align-middle" />
                AI · Web & Mobile App Development
              </p>
              <h1
                data-testid="hero-heading"
                className="font-display reveal text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] mt-8 text-[#0a0a0a]"
                style={{ animationDelay: "100ms" }}
              >
                App development,
                <br />
                <span className="text-[#a1a1aa]">solved by </span>
                <span className="text-[#1f87bc]">AI.</span>
              </h1>
              <p
                className="reveal mt-10 text-lg md:text-xl text-[#52525b] max-w-2xl leading-relaxed"
                style={{ animationDelay: "200ms" }}
                data-testid="hero-subtitle"
              >
                We build complex web & mobile apps in a week — not a quarter.
                Near-zero bugs. A fraction of agency cost. Zero human-error or
                delay risk. Just a market-ready product, fast.
              </p>

              <div
                className="reveal mt-12 flex flex-col sm:flex-row gap-4"
                style={{ animationDelay: "300ms" }}
              >
                <a
                  href={EMAIL_HREF}
                  data-testid="hero-cta-email"
                  className="inline-flex items-center justify-center gap-3 bg-[#1f87bc] hover:bg-[#176d99] text-white px-7 py-4 transition-colors font-medium"
                >
                  Start your project
                  <ArrowUpRight className="h-5 w-5" />
                </a>
                <Link
                  to="/about"
                  data-testid="hero-cta-about"
                  className="inline-flex items-center justify-center gap-3 border border-black/20 hover:border-black/60 text-[#0a0a0a] px-7 py-4 transition-colors font-medium"
                >
                  Why AI changes everything
                </Link>
              </div>

              <div
                className="reveal mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10"
                style={{ animationDelay: "400ms" }}
              >
                {[
                  ["Apps shipped", "60+"],
                  ["Avg. cycle", "7 days"],
                  ["Stack coverage", "Web + iOS + Android"],
                  ["Time zone", "Global"],
                ].map(([label, val]) => (
                  <div key={label} className="bg-[#faf9f6] px-5 py-6">
                    <p className="overline">{label}</p>
                    <p className="font-display text-2xl md:text-3xl mt-2 text-[#0a0a0a]">
                      {val}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <MarqueeStrip />

      {/* METRICS / USPs */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
        data-testid="usp-section"
      >
        <div className="grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-5">
            <p className="overline">Why AI</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 text-[#0a0a0a] leading-[1]">
              The math is brutal.
              <br />
              The math is yours.
            </h2>
            <p className="mt-8 text-[#52525b] leading-relaxed max-w-md">
              We don't just write code faster. We remove the entire class of
              risk that makes traditional app development slow, expensive, and
              unreliable.
            </p>
          </div>

          <div className="col-span-12 md:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/10">
              {METRICS.map((m) => {
                const Icon = m.icon;
                return (
                  <div
                    key={m.label}
                    data-testid={`metric-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="bg-white p-8 hover-lift hover:bg-[#fcfbf7]"
                  >
                    <Icon className="h-6 w-6 text-[#1f87bc]" />
                    <p className="font-display text-5xl mt-6 text-[#0a0a0a]">
                      {m.value}
                    </p>
                    <p className="mt-2 text-[#0a0a0a] font-medium">{m.label}</p>
                    <p className="mt-2 text-sm text-[#52525b] leading-relaxed">
                      {m.context}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section
        className="border-t border-black/10 bg-[#f3f1ec]"
        data-testid="process-section"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <p className="overline">How it works</p>
              <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 text-[#0a0a0a] leading-[1]">
                Four steps. Seven days.
              </h2>
            </div>
            <p className="text-[#52525b] max-w-md">
              A radically compressed timeline that still produces a real,
              production-grade application.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <div
                  key={s.n}
                  data-testid={`step-${s.n}`}
                  className="bg-white p-8 hover-lift hover:bg-[#fcfbf7] min-h-[260px] flex flex-col"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-xs tracking-[0.2em] text-[#1f87bc]">
                      {s.n}
                    </span>
                    <Icon className="h-5 w-5 text-[#a1a1aa]" />
                  </div>
                  <h3 className="font-display text-2xl mt-8 text-[#0a0a0a]">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-[#52525b] leading-relaxed">
                    {s.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* COMPARE */}
      <section
        className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32"
        data-testid="compare-section"
      >
        <div className="max-w-3xl">
          <p className="overline">The comparison</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl mt-6 text-[#0a0a0a] leading-[1]">
            AI-powered vs. the old way.
          </h2>
        </div>

        <div className="mt-14 border border-black/10">
          <div className="grid grid-cols-3 bg-white">
            <div className="p-5 overline text-[#71717a]">Dimension</div>
            <div className="p-5 overline text-[#1f87bc] border-l border-black/10">
              Pixelkare · AI
            </div>
            <div className="p-5 overline text-[#71717a] border-l border-black/10">
              Traditional agency
            </div>
          </div>
          {COMPARE.map(([dim, ai, trad], idx) => (
            <div
              key={dim}
              className={`grid grid-cols-3 border-t border-black/10 ${
                idx % 2 ? "bg-[#faf9f6]" : "bg-white"
              }`}
              data-testid={`compare-row-${idx}`}
            >
              <div className="p-5 text-[#52525b] text-sm md:text-base">
                {dim}
              </div>
              <div className="p-5 text-[#0a0a0a] border-l border-black/10 text-sm md:text-base font-medium">
                {ai}
              </div>
              <div className="p-5 text-[#71717a] border-l border-black/10 text-sm md:text-base">
                {trad}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="border-t border-black/10 bg-[#f3f1ec]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32 text-center">
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl text-[#0a0a0a] leading-[0.95]">
            Ship it{" "}
            <span className="text-[#1f87bc]">this week</span>.
          </h2>
          <p className="mt-8 text-[#52525b] max-w-xl mx-auto leading-relaxed">
            One conversation, one spec, one week. That's all it takes to go
            from idea to a live product.
          </p>
          <a
            href={EMAIL_HREF}
            data-testid="final-cta-email"
            className="mt-12 inline-flex items-center gap-3 bg-[#1f87bc] hover:bg-[#176d99] text-white px-8 py-4 font-medium transition-colors"
          >
            <Mail className="h-5 w-5" />
            Email us at hello@pixelkare.com
          </a>
        </div>
      </section>
    </div>
  );
}
